import { Component } from '@theme/component';
import { debounce, localizeUrl, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [commandLabel] - The keyboard shortcut command label element.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {((amount: number) => string) | null} */
  #money = null;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.addEventListener('click', this.#handleModalClick, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    }

    // Update keyboard shortcut label based on platform
    this.#updateCommandLabel();

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Updates the keyboard shortcut label based on the user's platform
   */
  #updateCommandLabel() {
    const { commandLabel } = this.refs;
    if (!commandLabel) return;

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    commandLabel.textContent = isMac ? '⌘ + K' : 'Ctrl + K';
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Voyado: notify the click when following a dropdown product (also covers the Enter-key .click() path).
    const voyadoItem = target.closest('[data-voyado-ticket]');
    if (voyadoItem instanceof HTMLElement && voyadoItem.dataset.voyadoTicket) {
      this.#voyadoNotifyClick(voyadoItem.dataset.voyadoTicket);
    }

    // Check if click is on a query suggestion
    const querySuggestion = target.closest('.predictive-search-results__card--query');
    if (querySuggestion) {
      event.preventDefault();
      this.#handleQuerySuggestionSelect(querySuggestion);
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles query suggestion selection by populating the input field
   * @param {HTMLElement} querySuggestion - The query suggestion element
   */
  #handleQuerySuggestionSelect(querySuggestion) {
    // Support both <a> (search results) and <span> (empty state) pill elements
    const pillElement = querySuggestion.querySelector('.pills__pill');
    if (!pillElement) return;

    const ariaLabel = pillElement.querySelector('[aria-label]')?.getAttribute('aria-label');
    const suggestionText = ariaLabel || pillElement.textContent?.trim() || '';

    if (suggestionText) {
      this.refs.searchInput.value = suggestionText;
      this.#showResetButton();
      this.#getSearchResults(suggestionText);
      this.refs.searchInput.focus();
    }
  }

  /**
   * Handles the CMD+K (Mac) or CTRL+K (Windows/Linux) key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list',
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          // Check if the current item is a query suggestion
          const currentItem = this.#currentItem;
          if (currentItem?.classList.contains('predictive-search-results__card--query')) {
            this.#handleQuerySuggestionSelect(currentItem);
          } else {
            // For other items (products, collections, etc.), follow the link
            currentItem?.querySelector('a')?.click();
          }
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (this.#isVoyado) {
      this.#getVoyadoResults(searchTerm);
      return;
    }

    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /** Whether instant search should be powered by Voyado Elevate. */
  get #isVoyado() {
    return (
      typeof Theme !== 'undefined' &&
      Theme.search?.provider === 'voyado' &&
      Boolean(/** @type {any} */ (window).elevate?.query)
    );
  }

  /**
   * Voyado-powered instant search: query the Elevate autocomplete endpoint and render
   * phrase suggestions + product suggestions into the dropdown. Uses a dynamic import so
   * non-Voyado stores (where @theme/voyado-client is not in the importmap) are unaffected.
   * @param {string} searchTerm
   */
  async #getVoyadoResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const { autocomplete } = await import('@theme/voyado-client');
      const data = await autocomplete({ q: searchTerm }, { contentLists: [{ id: 'content' }] });
      if (abortController.signal.aborted) return;

      predictiveSearchResults.replaceChildren(this.#buildVoyadoDropdown(/** @type {any} */ (data)));
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('[voyado] autocomplete failed', error);
    }
  }

  /**
   * Voyado instant-search start view (before typing / after clearing): recently viewed
   * (client-side) + trending products from the configured Voyado landing page. Replaces
   * the native empty state, so no Shopify fallback shows.
   */
  async #renderVoyadoEmptyState() {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    const dropdown = document.createElement('div');
    dropdown.className = 'predictive-search-dropdown';
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';
    inner.setAttribute('data-search-results', '');
    dropdown.appendChild(inner);

    // Recently viewed (the shopper's own browsing; provider-neutral). Wrap in the same
    // `.predictive-search-results__products` container native uses, so it gets the grid +
    // inline padding. Drop the "Clear" control — not wanted in the Voyado start view.
    const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
    if (abortController.signal.aborted) return;
    if (recentlyViewedMarkup) {
      const parsed = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewed = parsed.getElementById('predictive-search-products');
      if (recentlyViewed) {
        recentlyViewed.querySelector('.predictive-search-results__clear')?.remove();
        for (const child of recentlyViewed.children) {
          if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
        }
        const section = document.createElement('div');
        section.className = 'predictive-search-results__products';
        section.append(...recentlyViewed.children);
        inner.appendChild(section);
      }
    }

    // Trending: products from the configured Voyado "search start" landing page.
    const recommendationsPage = /** @type {any} */ (Theme).search?.recommendationsPage;
    if (recommendationsPage) {
      try {
        const { landingPage, normalizeList } = await import('@theme/voyado-client');
        const data = await landingPage({ pageReference: recommendationsPage });
        if (abortController.signal.aborted) return;
        const list = normalizeList(/** @type {any} */ (data)?.primaryList);
        const trending = this.#buildVoyadoProductList(list.products.slice(0, 8), this.dataset.productsLabel);
        if (trending) inner.appendChild(trending);
      } catch (error) {
        console.error('[voyado] recommendations failed', error);
      }
    }

    if (abortController.signal.aborted) return;
    predictiveSearchResults.replaceChildren(dropdown);
    this.#resetScrollPositions();
  }

  /**
   * Build the dropdown from a Voyado autocomplete response by cloning the Liquid-authored
   * <template>s and filling their data-voyado-slot hooks (markup stays in the snippet).
   * @param {any} data
   * @returns {HTMLElement}
   */
  #buildVoyadoDropdown(data) {
    const dropdown = document.createElement('div');
    dropdown.className = 'predictive-search-dropdown';
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';
    inner.setAttribute('data-search-results', '');
    dropdown.appendChild(inner);

    const phrases = (data.phraseSuggestions || []).slice(0, 6);
    const queryTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-query-template]')
    );
    if (phrases.length && queryTemplate?.content.firstElementChild) {
      const list = document.createElement('ul');
      list.className =
        'predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled';
      list.setAttribute('role', 'listbox');

      for (const phrase of phrases) {
        const item = /** @type {HTMLElement} */ (queryTemplate.content.firstElementChild.cloneNode(true));
        const link = /** @type {HTMLAnchorElement | null} */ (item.querySelector('[data-voyado-slot="link"]'));
        if (link) link.href = this.#voyadoSearchUrl(phrase.q);
        const label = /** @type {HTMLElement | null} */ (item.querySelector('[data-voyado-slot="query-label"]'));
        if (label) {
          label.setAttribute('aria-label', phrase.q);
          this.#fillHighlight(label, phrase.highlighted || phrase.q);
        }
        list.appendChild(item);
      }
      inner.appendChild(list);
    }

    const products = (data.productSuggestions || [])
      .flatMap((/** @type {any} */ group) => group.products || [])
      .slice(0, 6);
    const productList = this.#buildVoyadoProductList(products, this.dataset.productsLabel);
    if (productList) inner.appendChild(productList);

    return dropdown;
  }

  /**
   * Build a `#predictive-search-products` block from Voyado products by cloning the
   * Liquid product <template>. Shared by autocomplete results and the empty-state view.
   * @param {any[]} products
   * @param {string} [title]
   * @returns {HTMLElement | null}
   */
  #buildVoyadoProductList(products, title) {
    const productTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-voyado-product-template]')
    );
    if (!products?.length || !productTemplate?.content.firstElementChild) return null;

    const wrapper = document.createElement('div');
    wrapper.id = 'predictive-search-products';
    wrapper.className = 'predictive-search-results__products';

    if (title) {
      const heading = document.createElement('h4');
      heading.className = 'predictive-search-results__title';
      heading.textContent = title;
      wrapper.appendChild(heading);
    }

    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';
    list.setAttribute('role', 'listbox');

    for (const product of products) {
      const item = /** @type {HTMLElement} */ (productTemplate.content.firstElementChild.cloneNode(true));
      this.#fillVoyadoProduct(item, product);
      list.appendChild(item);
    }

    wrapper.appendChild(list);
    return wrapper;
  }

  /**
   * Fill a cloned product template from a Voyado product.
   * @param {HTMLElement} item
   * @param {any} product
   */
  #fillVoyadoProduct(item, product) {
    // Voyado links come back unlocalized — re-prefix so the PDP stays in the shopper's locale + market.
    const link = product.link ? localizeUrl(product.link) : '#';
    const title = product.title || product.name || '';

    // Stash the Elevate ticket so a click can be notified (see #handleModalClick).
    if (product.ticket) item.dataset.voyadoTicket = product.ticket;

    item.querySelectorAll('[data-voyado-slot="link"]').forEach((anchor) => anchor.setAttribute('href', link));

    const titleSr = item.querySelector('[data-voyado-slot="title-sr"]');
    if (titleSr) titleSr.textContent = title;

    const titleNode = item.querySelector('[data-voyado-slot="title"]');
    if (titleNode) titleNode.textContent = title;

    const image = /** @type {HTMLImageElement | null} */ (item.querySelector('[data-voyado-slot="image"]'));
    const imageUrl = product.imageInfo?.images?.[0]?.sources?.[0]?.url;
    if (image && imageUrl) {
      image.src = imageUrl;
      image.alt = title;
    }

    const priceNode = item.querySelector('[data-voyado-slot="price"]');
    if (!priceNode) return;

    // Converted market: Voyado's amount is the wrong number, so take the Shopify one.
    if (!this.#trustVoyadoPrices) {
      this.#injectPrice(priceNode, link).catch(() => {});
    } else if (product.sellingPrice?.min != null) {
      priceNode.textContent = this.#voyadoMoney(product.sellingPrice.min);
    }
  }

  /**
   * Fill a result's price from the product's `.js` (cents, already in the presented currency).
   * @param {Element} priceNode
   * @param {string} link
   */
  async #injectPrice(priceNode, link) {
    if (link === '#') return;

    const path = new URL(link, location.origin).pathname;
    const product = await fetch(`${path}.js`).then((response) => (response.ok ? response.json() : null));
    const format = (await this.#moneyFormat()) ?? ((/** @type {number} */ amount) => this.#voyadoMoney(amount));
    if (product?.price_min != null && priceNode.isConnected) {
      priceNode.textContent = format(product.price_min / 100);
    }
  }

  /**
   * Market money format, derived once from the Liquid sample; loaded lazily since @theme/voyado-client isn't in the importmap on non-Voyado stores.
   * @returns {Promise<((amount: number) => string) | null>}
   */
  async #moneyFormat() {
    const sample = this.dataset.moneySample;
    if (!sample) return null;
    if (!this.#money) {
      const { moneyFormatter } = await import('@theme/voyado-client');
      this.#money ??= moneyFormatter(sample);
    }
    return this.#money;
  }

  /**
   * Fire a Voyado dropdown-product click notification. Dynamic import so non-Voyado stores never load the client.
   * @param {string} ticket
   */
  #voyadoNotifyClick(ticket) {
    import('@theme/voyado-client').then(({ notify }) => notify('click', ticket)).catch(() => {});
  }

  /**
   * Set a query-suggestion label, wrapping Voyado's {matched} markers in <mark> via DOM nodes.
   * @param {HTMLElement} target
   * @param {string} highlighted
   */
  #fillHighlight(target, highlighted) {
    target.replaceChildren();
    for (const part of String(highlighted).split(/(\{.+?\})/)) {
      if (!part) continue;
      if (part.startsWith('{') && part.endsWith('}')) {
        const mark = document.createElement('mark');
        mark.textContent = part.slice(1, -1);
        target.appendChild(mark);
      } else {
        target.appendChild(document.createTextNode(part));
      }
    }
  }

  /**
   * @param {string} term
   * @returns {string}
   */
  #voyadoSearchUrl(term) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', term);
    return url.toString();
  }

  /**
   * @param {number} amount
   * @returns {string}
   */
  #voyadoMoney(amount) {
    const elevate = /** @type {any} */ (window).elevate;
    const locale = elevate?.query?.c?.locale || document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        // Safe for both callers: Voyado amounts only reach here when its currency is the presented one, and `.js` amounts always are.
        currency: this.dataset.currency || 'EUR',
      }).format(amount);
    } catch {
      return String(amount);
    }
  }

  /** Whether Voyado's prices are in the presented currency — it indexes one price per product in the shop currency and never converts. */
  // Currency only: see the same getter in voyado-grid.js for why an adjustment-only market is safe.
  get #trustVoyadoPrices() {
    // APC: Voyado indexes D2C prices; a B2B buyer's catalog price only comes from the product's `.js`.
    if (this.dataset.b2b === 'true') return false;
    const shopCurrency = this.dataset.shopCurrency;
    return !shopCurrency || shopCurrency === (this.dataset.currency || 'EUR');
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    // Voyado owns its own empty state (recently viewed + trending) — bypass the native
    // section render, which would otherwise leave a stale Voyado dropdown on clear.
    if (this.#isVoyado) {
      await this.#renderVoyadoEmptyState();
      return;
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
